import { FC, KeyboardEvent, useCallback } from "react";

import InputBase from "@mui/material/InputBase";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";

import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardIcon } from "@/dashboard-design-system/Icon";
import { DashboardTypography } from "@/dashboard-design-system/Typography";
import { ChevronLeft, ChevronRight, Cross, Search } from "@/icons/outlined";

const INPUT_HEIGHT = 36;
const INPUT_MIN_WIDTH = 206;
const COUNTER_MIN_WIDTH = 40;

export interface QuickAddSearchInputProps {
  query: string;
  onQueryChange: (next: string) => void;
  matchCount: number;
  currentMatchIndex: number;
  onPrevMatch: () => void;
  onNextMatch: () => void;
  onClose: () => void;
  searchPlaceholder: string;
  counterLabel: (params: { current: number; total: number }) => string;
  dataTest: string;
}

export const QuickAddSearchInput: FC<QuickAddSearchInputProps> = ({
  query,
  onQueryChange,
  matchCount,
  currentMatchIndex,
  onPrevMatch,
  onNextMatch,
  onClose,
  searchPlaceholder,
  counterLabel,
  dataTest,
}) => {
  const theme = useTheme();

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key === "Enter" && matchCount > 0) {
        event.preventDefault();
        if (event.shiftKey) {
          onPrevMatch();
        } else {
          onNextMatch();
        }
      }
    },
    [matchCount, onClose, onNextMatch, onPrevMatch]
  );

  return (
    <InputBase
      autoFocus
      value={query}
      onChange={(event) => onQueryChange(event.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={searchPlaceholder}
      inputProps={{ "aria-label": searchPlaceholder, "data-test": `${dataTest}-input` }}
      startAdornment={
        <DashboardIcon vibe="text-neutral-quiet" size="large" sx={{ marginRight: 1 }}>
          <Search />
        </DashboardIcon>
      }
      endAdornment={
        query ? (
          <Stack direction="row" alignItems="center" gap={0.5} sx={{ marginLeft: 1 }}>
            <DashboardIconButton
              variant="text"
              vibe="neutral"
              size="extraSmall"
              onClick={onPrevMatch}
              disabled={matchCount === 0}
              aria-label="Previous match"
              data-test={`${dataTest}-prev`}
            >
              <ChevronLeft />
            </DashboardIconButton>
            <DashboardTypography
              variant="caption"
              vibe="text-neutral-quiet"
              sx={{ minWidth: COUNTER_MIN_WIDTH, textAlign: "center", whiteSpace: "nowrap" }}
              data-test={`${dataTest}-counter`}
            >
              {counterLabel({ current: matchCount === 0 ? 0 : currentMatchIndex + 1, total: matchCount })}
            </DashboardTypography>
            <DashboardIconButton
              variant="text"
              vibe="neutral"
              size="extraSmall"
              onClick={onNextMatch}
              disabled={matchCount === 0}
              aria-label="Next match"
              data-test={`${dataTest}-next`}
            >
              <ChevronRight />
            </DashboardIconButton>
            <DashboardIconButton
              variant="text"
              vibe="neutral"
              size="extraSmall"
              onClick={onClose}
              aria-label="Close search"
              data-test={`${dataTest}-close`}
            >
              <Cross />
            </DashboardIconButton>
          </Stack>
        ) : null
      }
      sx={{
        flex: 1,
        minWidth: INPUT_MIN_WIDTH,
        height: INPUT_HEIGHT,
        paddingX: theme.spacing(1.5),
        borderRadius: theme.radius(1),
        backgroundColor: theme.palette.surface.neutral.white,
        border: `1px solid ${theme.palette.border.neutral.quiet}`,
        transition: "border-color 120ms ease, box-shadow 120ms ease",
        "& .MuiInputBase-input": {
          ...theme.typography.body2,
          color: theme.palette.text.neutral.moderate,
          padding: 0,
          height: "100%",
          "&::placeholder": {
            color: theme.palette.text.neutral.supershy,
            opacity: 1,
          },
        },
        "&.Mui-focused": {
          borderColor: theme.palette.border.brand.intense,
          boxShadow: `0 0 0 3px ${theme.palette.border.brand.moderate}`,
        },
      }}
    />
  );
};
